import axios from '../../../../config/axios'
import {
  ChecklistComment,
  ChecklistItem,
  ChecklistItemStatus,
  FileInChecklistItem,
  GeneralFile,
  ProductApprovalDetail,
  ProductApprovalListItem,
} from './types'

const GATEWAY_URL = process.env.REACT_APP_GATEWAY_BASE_URL
export const APPROVAL_API = `${GATEWAY_URL}/backend-fashion-orders/product-approval`

export interface CreateApprovalPayload {
  productId: number
  name: string
  type?: 'PILOT' | 'SHOWROOM'
  supplierCompanyId?: number | null
  approverId?: number | null
  startDate?: string | null
  expectedDeliveryDate?: string | null
  sizes?: string[]
}

export interface UpdateApprovalPayload {
  name?: string
  supplierCompanyId?: number | null
  approverId?: number | null
  startDate?: string | null
  expectedDeliveryDate?: string | null
  sizes?: string[]
  frontImageId?: number | null
  sideImageId?: number | null
  backImageId?: number | null
}

export const ApprovalApi = {
  list: async (productId: number) => {
    const { data } = await axios.get<ProductApprovalListItem[]>(APPROVAL_API, {
      params: { productId },
    })
    return data
  },

  getById: async (id: number) => {
    const { data } = await axios.get<ProductApprovalDetail>(`${APPROVAL_API}/${id}`)
    return data
  },

  create: async (payload: CreateApprovalPayload) => {
    const { data } = await axios.post<ProductApprovalListItem>(APPROVAL_API, payload)
    return data
  },

  update: async (id: number, payload: UpdateApprovalPayload) => {
    const { data } = await axios.patch<ProductApprovalListItem>(
      `${APPROVAL_API}/${id}`,
      payload,
    )
    return data
  },

  remove: async (id: number) => {
    await axios.delete(`${APPROVAL_API}/${id}`)
  },

  promote: async (id: number) => {
    const { data } = await axios.post<ProductApprovalListItem>(
      `${APPROVAL_API}/${id}/promote`,
    )
    return data
  },

  uploadFile: async (file: File): Promise<GeneralFile> => {
    const formData = new FormData()
    formData.append('file', file)
    const { data } = await axios.post<GeneralFile>(`${APPROVAL_API}/upload`, formData)
    return data
  },

  addChecklistItem: async (
    approvalId: number,
    payload: { description: string; approverId?: number | null; position?: number },
  ) => {
    const { data } = await axios.post<ChecklistItem>(
      `${APPROVAL_API}/${approvalId}/checklist`,
      payload,
    )
    return data
  },

  updateChecklistItem: async (
    itemId: number,
    payload: { description?: string; approverId?: number | null; position?: number },
  ) => {
    const { data } = await axios.patch<ChecklistItem>(
      `${APPROVAL_API}/checklist/${itemId}`,
      payload,
    )
    return data
  },

  removeChecklistItem: async (itemId: number) => {
    await axios.delete(`${APPROVAL_API}/checklist/${itemId}`)
  },

  decideChecklistItem: async (itemId: number, status: ChecklistItemStatus) => {
    const { data } = await axios.post<ChecklistItem>(
      `${APPROVAL_API}/checklist/${itemId}/decision`,
      { status },
    )
    return data
  },

  addChecklistComment: async (itemId: number, comment: string) => {
    const { data } = await axios.post<ChecklistComment>(
      `${APPROVAL_API}/checklist/${itemId}/comment`,
      { comment },
    )
    return data
  },

  removeChecklistComment: async (commentId: number) => {
    await axios.delete(`${APPROVAL_API}/checklist/comment/${commentId}`)
  },

  addChecklistFile: async (itemId: number, file: File): Promise<FileInChecklistItem> => {
    const formData = new FormData()
    formData.append('file', file)
    const { data } = await axios.post<FileInChecklistItem>(
      `${APPROVAL_API}/checklist/${itemId}/file`,
      formData,
    )
    return data
  },

  removeChecklistFile: async (fileInItemId: number) => {
    await axios.delete(`${APPROVAL_API}/checklist/file/${fileInItemId}`)
  },
}
